// API Service for TCF Canada
// Centralized API calls replacing LocalStorage

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== CACHE SYSTEM ====================
// Simple in-memory cache with expiration

const cache = new Map();
const CACHE_DURATION = 60000; // 1 minute cache

const getCached = (key) => {
  const item = cache.get(key);
  if (!item) return null;
  if (Date.now() > item.expiry) {
    cache.delete(key);
    return null;
  }
  return item.data;
};

const setCache = (key, data, duration = CACHE_DURATION) => {
  cache.set(key, {
    data,
    expiry: Date.now() + duration
  });
};

const clearCache = (pattern) => {
  if (!pattern) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  }
};

// Get token from localStorage
const getToken = () => localStorage.getItem('tcf-auth-token');

// Rewrite a /api/uploads/audio|images|tts URL to the auth-gated /api/media/ endpoint.
// Also handles already-rewritten /api/media/ URLs with stale baked-in tokens.
// Logo and notifications are intentionally left public.
export const getMediaUrl = (url) => {
  if (!url) return url;
  const token = getToken();
  const uploadPrefixes = ['/api/uploads/audio', '/api/uploads/images', '/api/uploads/tts'];
  const mediaPrefixes  = ['/api/media/audio',   '/api/media/images',   '/api/media/tts'];
  let basePart = '';
  let pathPart = url;
  try {
    const u = new URL(url);
    basePart = u.origin;
    pathPart = u.pathname; // strip any existing query string
  } catch {
    pathPart = url.split('?')[0]; // relative URL — strip stale token if present
  }
  if (uploadPrefixes.some(p => pathPart.startsWith(p))) {
    const mediaPath = pathPart.replace('/api/uploads/', '/api/media/');
    return token ? `${basePart}${mediaPath}?token=${token}` : `${basePart}${mediaPath}`;
  }
  if (mediaPrefixes.some(p => pathPart.startsWith(p))) {
    // Already in /api/media/ format but token may be stale — replace with current token
    return token ? `${basePart}${pathPart}?token=${token}` : `${basePart}${pathPart}`;
  }
  return url;
};

// Set token in localStorage
const setToken = (token) => localStorage.setItem('tcf-auth-token', token);

// Remove token
const removeToken = () => localStorage.removeItem('tcf-auth-token');

// Headers with auth
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// ==================== AUTH ====================

export const authAPI = {
  async login(username, password) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await response.json();
    if (data.success && data.token) {
      setToken(data.token);
      localStorage.setItem('tcf-current-user', JSON.stringify(data.user));
    }
    return data;
  },

  async register(userData) {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    return response.json();
  },

  async googleCallback(sessionId) {
    const response = await fetch(`${API_URL}/api/auth/google/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    });
    const data = await response.json();
    if (data.success && data.token) {
      setToken(data.token);
      localStorage.setItem('tcf-current-user', JSON.stringify(data.user));
    }
    return data;
  },

  async getMe() {
    const token = getToken();
    if (!token) return { success: false };
    
    const response = await fetch(`${API_URL}/api/auth/me?token=${token}`);
    return response.json();
  },

  async getSessions(adminId) {
    const response = await fetch(`${API_URL}/api/auth/sessions/${adminId}`);
    return response.json();
  },

  async getAllActiveSessions() {
    const response = await fetch(`${API_URL}/api/auth/sessions/all/active`);
    return response.json();
  },

  async logout(userId = null) {
    const token = getToken();
    // Notify backend about logout
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, user_id: userId })
      });
    } catch (error) {
      console.error('Logout API error:', error);
    }
    removeToken();
    localStorage.removeItem('tcf-current-user');
  },

  getCurrentUser() {
    const user = localStorage.getItem('tcf-current-user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated() {
    return !!getToken();
  },

  async forgotPassword(email) {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return response.json();
  },

  async resetPassword(email, code, new_password) {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, new_password })
    });
    return response.json();
  }
};

// ==================== USERS ====================

export const usersAPI = {
  async getAll() {
    const response = await fetch(`${API_URL}/api/users/`);
    return response.json();
  },

  async getAllStudents() {
    const response = await fetch(`${API_URL}/api/users/all-students`);
    return response.json();
  },

  async getById(userId) {
    const response = await fetch(`${API_URL}/api/users/user/${userId}`);
    return response.json();
  },

  async getByCenter(adminId) {
    const response = await fetch(`${API_URL}/api/users/by-center/${adminId}`);
    return response.json();
  },

  async getAdmins() {
    const response = await fetch(`${API_URL}/api/users/admins`);
    return response.json();
  },

  async getGroupedByCenter() {
    const response = await fetch(`${API_URL}/api/users/grouped-by-center`);
    return response.json();
  },

  async getUserByInvitationCode(code) {
    const response = await fetch(`${API_URL}/api/users/by-invitation-code/${code}`);
    if (response.ok) {
      return response.json();
    }
    return null;
  },

  async promoteToAdmin(userId) {
    const response = await fetch(`${API_URL}/api/users/promote/${userId}`, {
      method: 'POST'
    });
    return response.json();
  },

  async demoteFromAdmin(userId) {
    const response = await fetch(`${API_URL}/api/users/demote/${userId}`, {
      method: 'POST'
    });
    return response.json();
  },

  async toggleCenterBlock(adminId) {
    const response = await fetch(`${API_URL}/api/users/center/${adminId}/toggle-block`, {
      method: 'POST'
    });
    return response.json();
  },

  async assignToCenter(userId, adminId) {
    const response = await fetch(`${API_URL}/api/users/assign/${userId}/to/${adminId}`, {
      method: 'POST'
    });
    return response.json();
  },

  async removeFromCenter(userId) {
    const response = await fetch(`${API_URL}/api/users/remove-from-center/${userId}`, {
      method: 'POST'
    });
    return response.json();
  },

  async update(userId, updates) {
    const response = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async updatePassword(userId, currentPassword, newPassword) {
    const response = await fetch(`${API_URL}/api/users/${userId}/password?current_password=${encodeURIComponent(currentPassword)}&new_password=${encodeURIComponent(newPassword)}`, {
      method: 'PUT'
    });
    return response.json();
  },

  async archive(userId) {
    const response = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  async deleteUser(userId) {
    const response = await fetch(`${API_URL}/api/users/${userId}/permanent`, {
      method: 'DELETE'
    });
    return response.json();
  },

  async restore(userId) {
    const response = await fetch(`${API_URL}/api/users/${userId}/restore`, {
      method: 'POST'
    });
    return response.json();
  },

  async getArchivedUsers() {
    const response = await fetch(`${API_URL}/api/users/archived`);
    return response.json();
  },

  async findByInvitationCode(code) {
    const response = await fetch(`${API_URL}/api/users/admin-by-code/${code}`);
    return response.json();
  },
  
  // Alias for findByInvitationCode
  async getAdminByCode(code) {
    const response = await fetch(`${API_URL}/api/users/admin-by-code/${code}`);
    return response.json();
  }
};

// ==================== SETTINGS ====================

export const settingsAPI = {
  async getSiteSettings() {
    // Check cache first
    const cached = getCached('site_settings');
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/settings/site`);
    const data = await response.json();
    setCache('site_settings', data, 300000); // Cache for 5 minutes
    return data;
  },

  async updateSiteSettings(updates) {
    clearCache('site_settings');
    const response = await fetch(`${API_URL}/api/settings/site`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async getCenterSettings(adminId) {
    const cacheKey = `center_settings_${adminId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}`);
    const data = await response.json();
    setCache(cacheKey, data, 300000); // Cache for 5 minutes
    return data;
  },

  async updateCenterSettings(adminId, updates) {
    clearCache(`center_settings_${adminId}`);
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async getEffectiveSettings(userId) {
    const cacheKey = `effective_settings_${userId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/settings/effective/${userId}`);
    const data = await response.json();
    setCache(cacheKey, data, 300000); // Cache for 5 minutes
    return data;
  },

  // Notifications
  async getNotifications() {
    const response = await fetch(`${API_URL}/api/settings/notifications`);
    return response.json();
  },

  async createNotification(notification) {
    const response = await fetch(`${API_URL}/api/settings/notifications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(notification)
    });
    return response.json();
  },

  async markNotificationRead(notifId, userId) {
    const response = await fetch(`${API_URL}/api/settings/notifications/${notifId}/read/${userId}`, {
      method: 'PUT'
    });
    return response.json();
  },

  async deleteNotification(notifId) {
    const response = await fetch(`${API_URL}/api/settings/notifications/${notifId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  // Packs
  async getPacks() {
    const cached = getCached('packs');
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/settings/packs`);
    const data = await response.json();
    setCache('packs', data, 300000); // Cache for 5 minutes
    return data;
  },

  async updatePack(packId, updates) {
    clearCache('packs');
    const response = await fetch(`${API_URL}/api/settings/packs/${packId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async createPack(pack) {
    clearCache('packs');
    const response = await fetch(`${API_URL}/api/settings/packs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pack)
    });
    return response.json();
  },

  async deletePack(packId) {
    clearCache('packs');
    const response = await fetch(`${API_URL}/api/settings/packs/${packId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  // ==================== CENTER-SPECIFIC (Admin) ====================

  // Center Packs
  async getCenterPacks(adminId) {
    const cacheKey = `center_packs_${adminId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/packs`);
    const data = await response.json();
    setCache(cacheKey, data, 300000);
    return data;
  },

  async updateCenterPack(adminId, packId, updates) {
    clearCache(`center_packs_${adminId}`);
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/packs/${packId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async createCenterPack(adminId, pack) {
    clearCache(`center_packs_${adminId}`);
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/packs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pack)
    });
    return response.json();
  },

  async deleteCenterPack(adminId, packId) {
    clearCache(`center_packs_${adminId}`);
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/packs/${packId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  // Center Testimonials
  async getCenterTestimonials(adminId) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/testimonials`);
    return response.json();
  },

  async createTestimonial(adminId, data) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/testimonials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async updateTestimonial(adminId, testiId, data) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/testimonials/${testiId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async deleteTestimonial(adminId, testiId) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/testimonials/${testiId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  async uploadTestimonialPhoto(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/upload/testimonial-photo`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  },

  // Center Notifications
  async getCenterNotifications(adminId) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/notifications`);
    return response.json();
  },

  async createCenterNotification(adminId, notification) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/notifications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(notification)
    });
    return response.json();
  },

  async markCenterNotificationRead(adminId, notifId, userId) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/notifications/${notifId}/read/${userId}`, {
      method: 'PUT'
    });
    return response.json();
  },

  async deleteCenterNotification(adminId, notifId) {
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/notifications/${notifId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  // Center Access Config
  async getCenterAccessConfig(adminId) {
    const cacheKey = `center_access_${adminId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/access`);
    const data = await response.json();
    setCache(cacheKey, data, 300000);
    return data;
  },

  async updateCenterAccessConfig(adminId, updates) {
    clearCache(`center_access_${adminId}`);
    const response = await fetch(`${API_URL}/api/settings/center/${adminId}/access`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  // Effective settings for users
  async getEffectivePacks(userId) {
    const response = await fetch(`${API_URL}/api/settings/effective/packs/${userId}`);
    return response.json();
  },

  async getEffectiveNotifications(userId) {
    const response = await fetch(`${API_URL}/api/settings/effective/notifications/${userId}`);
    return response.json();
  },

  async getEffectiveAccessConfig(userId) {
    const response = await fetch(`${API_URL}/api/settings/effective/access/${userId}`);
    return response.json();
  },

  async markEffectiveNotificationRead(notifId, userId) {
    const response = await fetch(`${API_URL}/api/settings/effective/notifications/${notifId}/read/${userId}`, {
      method: 'PUT'
    });
    return response.json();
  },

  async getSmtpSettings() {
    const response = await fetch(`${API_URL}/api/settings/smtp`);
    return response.json();
  },

  async updateSmtpSettings(data) {
    const response = await fetch(`${API_URL}/api/settings/smtp`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async testSmtp(toEmail) {
    const response = await fetch(`${API_URL}/api/settings/smtp/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to_email: toEmail })
    });
    return response.json();
  }
};

// ==================== CONTENT ====================

export const contentAPI = {
  // Series (Compréhension Orale & Écrite)
  async getSeriesSummary(module) {
    const cacheKey = `series_summary_${module}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    const response = await fetch(`${API_URL}/api/content/series/${module}?summary=true`);
    const data = await response.json();
    setCache(cacheKey, data, 300000); // Cache for 5 minutes
    return data;
  },

  async getSeries(module) {
    const cacheKey = `series_${module}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const response = await fetch(`${API_URL}/api/content/series/${module}`);
    const data = await response.json();
    setCache(cacheKey, data, 300000); // Cache for 5 minutes
    return data;
  },

  async getSerie(module, serieId) {
    const cacheKey = `serie_${module}_${serieId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/content/series/${module}/${serieId}`);
    const data = await response.json();
    setCache(cacheKey, data, 60000); // Cache for 1 minute
    return data;
  },

  async createSerie(module, serie) {
    clearCache(`series_${module}`);
    const response = await fetch(`${API_URL}/api/content/series/${module}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(serie)
    });
    return response.json();
  },

  async updateSerie(module, serieId, updates) {
    clearCache(`series_${module}`);
    clearCache(`serie_${module}_${serieId}`);
    const response = await fetch(`${API_URL}/api/content/series/${module}/${serieId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async deleteSerie(module, serieId) {
    clearCache(`series_${module}`);
    clearCache(`serie_${module}_${serieId}`);
    const response = await fetch(`${API_URL}/api/content/series/${module}/${serieId}`, {
      method: 'DELETE'
    });
    return response.json();
  },

  // Blocs (Expression Orale & Écrite)
  async getBlocs(module) {
    const cacheKey = `blocs_${module}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/content/blocs/${module}`);
    const data = await response.json();
    setCache(cacheKey, data, 60000); // Cache for 1 minute
    return data;
  },

  async getBloc(module, blocId) {
    const cacheKey = `bloc_${module}_${blocId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    
    const response = await fetch(`${API_URL}/api/content/blocs/${module}/${blocId}`);
    const data = await response.json();
    setCache(cacheKey, data, 60000); // Cache for 1 minute
    return data;
  },

  async createBloc(module, bloc) {
    clearCache(`blocs_${module}`);
    const response = await fetch(`${API_URL}/api/content/blocs/${module}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bloc)
    });
    return response.json();
  },

  async updateBloc(module, blocId, updates) {
    clearCache(`blocs_${module}`);
    clearCache(`bloc_${module}_${blocId}`);
    const response = await fetch(`${API_URL}/api/content/blocs/${module}/${blocId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  async deleteBloc(module, blocId) {
    clearCache(`blocs_${module}`);
    clearCache(`bloc_${module}_${blocId}`);
    const response = await fetch(`${API_URL}/api/content/blocs/${module}/${blocId}`, {
      method: 'DELETE'
    });
    return response.json();
  }
};

// ==================== UPLOADS ====================

export const trialsAPI = {
  async getBalance(userId) {
    const response = await fetch(`${API_URL}/api/trials/balance/${encodeURIComponent(userId)}`);
    return response.json();
  },

  async distribute(adminId, studentId, amount) {
    const response = await fetch(
      `${API_URL}/api/trials/distribute?admin_id=${encodeURIComponent(adminId)}&student_id=${encodeURIComponent(studentId)}&amount=${amount}`,
      { method: 'POST' }
    );
    return response.json();
  },

  async assignTrials(adminId, amount) {
    const response = await fetch(`${API_URL}/api/trials/assign?admin_id=${encodeURIComponent(adminId)}&amount=${amount}`, {
      method: 'POST',
    });
    return response.json();
  },

  async distributePack(adminId, userId, packId) {
    const response = await fetch(
      `${API_URL}/api/trials/distribute-pack?admin_id=${encodeURIComponent(adminId)}&user_id=${encodeURIComponent(userId)}&pack_id=${encodeURIComponent(packId)}`,
      { method: 'POST' }
    );
    return response.json();
  },

  async buyUserPack(userId, packId) {
    const response = await fetch(
      `${API_URL}/api/trials/buy-user-pack?user_id=${encodeURIComponent(userId)}&pack_id=${encodeURIComponent(packId)}`,
      { method: 'POST' }
    );
    return response.json();
  },

  async confirmUserPack(paypalOrderId, purchaseId) {
    const response = await fetch(
      `${API_URL}/api/trials/confirm-user-pack?paypal_order_id=${encodeURIComponent(paypalOrderId)}&purchase_id=${encodeURIComponent(purchaseId)}`,
      { method: 'POST' }
    );
    return response.json();
  },
};

export const uploadsAPI = {
  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/uploads/image/`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  },

  async uploadVideo(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/uploads/video/`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  },

  async uploadAudio(file, blocId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bloc_id', blocId);
    
    const response = await fetch(`${API_URL}/api/upload/audio/`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  }
};

export default {
  auth: authAPI,
  users: usersAPI,
  settings: settingsAPI,
  content: contentAPI,
  uploads: uploadsAPI
};
